import { z } from 'zod';
import { paginationInput, sortInput, idInput } from './common';

// Pagination input scoped by evento (used in listPatrocinadores/listGastos/listAnexos)
export const listEventoChildInput = paginationInput.extend({
  id: z.string().uuid(),
});

// Filtros de inscrições
export const listInscricoesInput = paginationInput.extend({
  id: z.string().uuid(),
  search: z.string().optional(),
  clube: z.string().optional(),
  lote: z.string().optional(),
  comRestricaoAlimentar: z.boolean().optional(),
});

// Enums
export const inscricaoStatusEnum = z.enum(['PENDENTE', 'PAGO', 'CANCELADO']);
export const patrocinadorTipoEnum = z.enum(['DIAMANTE', 'OURO', 'PRATA', 'BRONZE', 'APOIO']);
export const patrocinadorStatusEnum = z.enum(['PENDENTE', 'RECEBIDO', 'PERMUTA']);
export const anexoCategoriaEnum = z.enum(['RECIBO', 'IMAGEM', 'DOCUMENTO']);
// Evento
export const listEventosInput = paginationInput.merge(sortInput).extend({
  search: z.string().optional(),
  ativo: z.boolean().optional(),
});

/**
 * Campos vazios do formulário (`""` de input text/date, `NaN` de input number com
 * valueAsNumber) chegariam como valor inválido nas colunas date/decimal/integer e
 * estourariam erro no Postgres. Normaliza para `undefined` antes de validar.
 */
const vazioParaUndefined = <T extends z.ZodTypeAny>(schema: T) =>
  z.preprocess(
    (valor) =>
      valor === '' || valor === null || (typeof valor === 'number' && Number.isNaN(valor))
        ? undefined
        : valor,
    schema
  );

const dataObrigatoria = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Informe uma data válida');

export const createEventoInput = z.object({
  nome: z.string().min(3, 'Nome deve ter no mínimo 3 caracteres'),
  descricao: z.string().optional(),
  local: z.string().optional(),
  dataInicio: dataObrigatoria,
  dataFim: dataObrigatoria,
  valorInscricao: vazioParaUndefined(z.string().optional()),
  limiteParticipantes: vazioParaUndefined(z.number().int().positive().optional()),
});

export const updateEventoInput = idInput.extend({
  nome: z.string().min(3).optional(),
  descricao: z.string().optional(),
  local: z.string().optional(),
  dataInicio: vazioParaUndefined(dataObrigatoria.optional()),
  dataFim: vazioParaUndefined(dataObrigatoria.optional()),
  valorInscricao: vazioParaUndefined(z.string().nullable().optional()),
  limiteParticipantes: vazioParaUndefined(z.number().int().positive().nullable().optional()),
  ativo: z.boolean().optional(),
});

// Inscrição
export const createInscricaoInput = z.object({
  eventoId: z.string().uuid(),
  nome: z.string().min(2, 'Nome deve ter no mínimo 2 caracteres'),
  clube: z.string().optional(),
  restricaoAlimentar: z.string().optional(),
  lote: z.string().optional(),
  valor: z.string().optional(),
  statusPagamento: inscricaoStatusEnum.default('PENDENTE'),
});

export const updateInscricaoInput = idInput.extend({
  nome: z.string().min(2).optional(),
  clube: z.string().optional(),
  restricaoAlimentar: z.string().optional(),
  lote: z.string().optional(),
  valor: z.string().optional(),
  statusPagamento: inscricaoStatusEnum.optional(),
});

// Importação de inscrições via planilha
export const importInscricaoItem = z.object({
  nome: z.string().min(2),
  clube: z.string().optional().nullable(),
  restricaoAlimentar: z.string().optional().nullable(),
  lote: z.string().optional().nullable(),
  valor: z.string().optional().nullable(),
});

export const importInscricoesPreviewInput = z.object({
  eventoId: z.string().uuid(),
  items: z.array(importInscricaoItem),
});

export const importInscricoesCommitInput = z.object({
  eventoId: z.string().uuid(),
  statusPagamentoPadrao: z.enum(['PAGO', 'PENDENTE']).default('PAGO'),
  criar: z.array(importInscricaoItem),
  atualizar: z.array(
    z.object({
      id: z.string().uuid(),
      nome: z.string().optional(),
      clube: z.string().optional().nullable(),
      restricaoAlimentar: z.string().optional().nullable(),
      lote: z.string().optional().nullable(),
      valor: z.string().optional().nullable(),
    }),
  ),
  // Inscrições existentes a remover (ausentes da planilha), opt-in pelo usuário
  remover: z.array(z.string().uuid()).default([]),
});

// Patrocinador
export const createPatrocinadorInput = z.object({
  eventoId: z.string().uuid(),
  nome: z.string().min(2, 'Nome deve ter no mínimo 2 caracteres'),
  cnpjCpf: z.string().optional(),
  valor: z.string().min(1, 'Valor é obrigatório'),
  tipo: patrocinadorTipoEnum.default('APOIO'),
  observacao: z.string().optional(),
  linkAnexo: z.string().optional(),
  statusPatrocinio: patrocinadorStatusEnum.default('PENDENTE'),
});

export const updatePatrocinadorInput = idInput.extend({
  nome: z.string().min(2).optional(),
  cnpjCpf: z.string().optional(),
  valor: z.string().optional(),
  tipo: patrocinadorTipoEnum.optional(),
  observacao: z.string().optional(),
  linkAnexo: z.string().optional(),
});

export const alterarStatusPatrocinadorInput = idInput.extend({
  statusPatrocinio: patrocinadorStatusEnum,
});

// Gasto
export const createGastoInput = z.object({
  eventoId: z.string().uuid(),
  descricao: z.string().min(3, 'Descrição deve ter no mínimo 3 caracteres'),
  valor: z.string().min(1, 'Valor é obrigatório'),
  categoria: z.string().optional(),
  dataVencimento: z.string(),
});

export const updateGastoInput = idInput.extend({
  descricao: z.string().min(3).optional(),
  valor: z.string().optional(),
  categoria: z.string().optional(),
  dataVencimento: z.string().optional(),
});

// Anexo
export const createAnexoInput = z.object({
  eventoId: z.string().uuid(),
  nomeArquivo: z.string().min(1, 'Nome do arquivo é obrigatório'),
  urlArquivo: z.string().url('URL inválida'),
  tipoArquivo: z.string().optional(),
  categoria: anexoCategoriaEnum.default('DOCUMENTO'),
});

// Output types
export const eventoOutput = z.object({
  id: z.string(),
  nome: z.string(),
  descricao: z.string().nullable(),
  local: z.string().nullable(),
  dataInicio: z.string(),
  dataFim: z.string(),
  valorInscricao: z.string().nullable(),
  limiteParticipantes: z.number().nullable(),
  ativo: z.boolean(),
  createdAt: z.date(),
});

// Types
export type ListEventosInput = z.infer<typeof listEventosInput>;
export type CreateEventoInput = z.infer<typeof createEventoInput>;
export type UpdateEventoInput = z.infer<typeof updateEventoInput>;
export type EventoOutput = z.infer<typeof eventoOutput>;

export type ListEventoChildInput = z.infer<typeof listEventoChildInput>;
export type ListInscricoesInput = z.infer<typeof listInscricoesInput>;
export type InscricaoStatus = z.infer<typeof inscricaoStatusEnum>;
export type CreateInscricaoInput = z.infer<typeof createInscricaoInput>;
export type UpdateInscricaoInput = z.infer<typeof updateInscricaoInput>;
export type ImportInscricaoItem = z.infer<typeof importInscricaoItem>;
export type ImportInscricoesPreviewInput = z.infer<typeof importInscricoesPreviewInput>;
export type ImportInscricoesCommitInput = z.infer<typeof importInscricoesCommitInput>;

export type PatrocinadorTipo = z.infer<typeof patrocinadorTipoEnum>;
export type CreatePatrocinadorInput = z.infer<typeof createPatrocinadorInput>;
export type UpdatePatrocinadorInput = z.infer<typeof updatePatrocinadorInput>;

export type CreateGastoInput = z.infer<typeof createGastoInput>;
export type UpdateGastoInput = z.infer<typeof updateGastoInput>;

export type AnexoCategoria = z.infer<typeof anexoCategoriaEnum>;
export type CreateAnexoInput = z.infer<typeof createAnexoInput>;

